"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./waffle-grid.css";

export type WaffleCellTone = "empty" | "gold" | "partial" | "target" | "rest";

export type WaffleCell = {
  tone: WaffleCellTone;
  label?: string;
  value?: string;
  partialHeight?: number;
};

interface WaffleGridProps {
  cells: WaffleCell[];
  columns?: 10 | 20;
  className?: string;
  ariaLabel?: string;
}

export default function WaffleGrid({
  cells,
  columns = 20,
  className,
  ariaLabel,
}: WaffleGridProps): React.ReactElement {
  const interactive = cells.some((cell) => cell.label != null && cell.value != null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    label: "",
    value: "",
    x: 0,
    y: 0,
  });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toneClass = (tone: WaffleCellTone): string => {
    if (tone === "empty") return "";
    return ` waffle-grid-cell--${tone}`;
  };

  return (
    <>
      <div
        className={[
          "waffle-grid-root",
          `waffle-grid-root--cols-${columns}`,
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        role={interactive ? undefined : "img"}
        aria-label={interactive ? undefined : ariaLabel}
      >
        {cells.map((cell, index) => {
          const cellClass = `waffle-grid-cell${toneClass(cell.tone)}${
            interactive ? " waffle-grid-cell--interactive" : ""
          }`;
          const aria = cell.label && cell.value ? `${cell.label}: ${cell.value}` : undefined;

          if (interactive) {
            return (
              <button
                key={index}
                type="button"
                className={cellClass}
                aria-label={aria}
                onMouseEnter={(event) => {
                  if (!cell.label || !cell.value) return;
                  setTooltip({
                    visible: true,
                    label: cell.label,
                    value: cell.value,
                    x: event.clientX + 12,
                    y: event.clientY + 12,
                  });
                }}
                onMouseMove={(event) => {
                  setTooltip((current) =>
                    current.visible
                      ? {
                          ...current,
                          x: event.clientX + 12,
                          y: event.clientY + 12,
                        }
                      : current,
                  );
                }}
                onMouseLeave={() =>
                  setTooltip((current) => ({ ...current, visible: false }))
                }
              >
                {cell.tone === "partial" && cell.partialHeight ? (
                  <div
                    className="waffle-grid-fill"
                    style={{ height: `${cell.partialHeight}%` }}
                  />
                ) : null}
              </button>
            );
          }

          return <div key={index} className={cellClass} />;
        })}
      </div>

      {mounted && tooltip.visible
        ? createPortal(
            <div
              className="waffle-grid-tooltip"
              style={{ left: tooltip.x, top: tooltip.y }}
              role="tooltip"
            >
              <div className="waffle-grid-tooltip-label">{tooltip.label}</div>
              <div className="waffle-grid-tooltip-value">{tooltip.value}</div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
